import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { useNavigate, useParams } from "react-router-dom";
import useToast from "../../../../components/common/useToast";
import apiFunctions from "../../../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler } from "../../../../utils/HelperFunctions";
import WhatsAppIcon from "../../../../components/common/WhatsAppIcon";
import { Skeleton } from "primereact/skeleton";
import { ArrowLeftIcon, BellIcon } from "@heroicons/react/24/outline";

type PickerUser = {
    id: string;
    name: string;
    surname: string;
    mobileno: string;
};

const PICKER_PAGE_SIZE = 50;

const EventCreate: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();

    const [loading, setLoading] = useState(!!id);
    const [saving, setSaving] = useState(false);

    const [formName, setFormName] = useState("");
    const [formNameGu, setFormNameGu] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [formDate, setFormDate] = useState<Date | null>(null);
    const [formTime, setFormTime] = useState("");
    const [formVenue, setFormVenue] = useState("");
    const [formVenueGu, setFormVenueGu] = useState("");

    /* notify options — only offered while creating a new event */
    const [notifyPush, setNotifyPush] = useState(false);
    const [notifyWhatsApp, setNotifyWhatsApp] = useState(false);
    const [notifyAllUsers, setNotifyAllUsers] = useState(true);
    const [selectedUsers, setSelectedUsers] = useState<PickerUser[]>([]);

    /* user picker (infinite scroll) */
    const [pickerUsers, setPickerUsers] = useState<PickerUser[]>([]);
    const [pickerSearch, setPickerSearch] = useState("");
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerLoadingMore, setPickerLoadingMore] = useState(false);
    const [pickerPage, setPickerPage] = useState(0);
    const [pickerTotal, setPickerTotal] = useState(0);

    useEffect(() => {
        if (!id) return;

        (async () => {
            try {
                setLoading(true);
                const response = await apiFunctions.get(`/event/${id}`);
                if (response?.data?.success) {
                    const ev = response.data.data;
                    setFormName(ev.name || "");
                    setFormNameGu(ev.namegujarati || "");
                    setFormDescription(ev.description || "");
                    setFormDate(ev.event_date ? new Date(ev.event_date) : null);
                    setFormTime(ev.event_time || "");
                    setFormVenue(ev.venue || "");
                    setFormVenueGu(ev.venuegujarati || "");
                } else {
                    showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Failed to load event", life: 3000 });
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /* load a page of users for the picker (first page resets the list, later pages append) */
    async function loadPickerUsers(search: string, page: number) {
        try {
            if (page === 0) setPickerLoading(true);
            else setPickerLoadingMore(true);

            const url = search.trim().length > 1
                ? `/user/list/${page * PICKER_PAGE_SIZE}/${PICKER_PAGE_SIZE}/createddate/desc/${encodeURIComponent(search.trim())}`
                : `/user/list/${page * PICKER_PAGE_SIZE}/${PICKER_PAGE_SIZE}/createddate/desc`;

            const response = await apiFunctions.get(url);
            if (response?.data?.success) {
                setPickerUsers((prev) => (page === 0 ? response.data.data || [] : [...prev, ...(response.data.data || [])]));
                setPickerTotal(response.data.totalRecords || 0);
                setPickerPage(page);
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setPickerLoading(false);
            setPickerLoadingMore(false);
        }
    }

    /* fetch picker list (page 0) when "selected users" mode opens, and again on search */
    useEffect(() => {
        if (!id && (notifyPush || notifyWhatsApp) && !notifyAllUsers) {
            loadPickerUsers(pickerSearch, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notifyAllUsers, pickerSearch, notifyPush, notifyWhatsApp]);

    // load the next page once the user scrolls near the bottom of the picker list
    const handlePickerScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        if (nearBottom && !pickerLoading && !pickerLoadingMore && pickerUsers.length < pickerTotal) {
            loadPickerUsers(pickerSearch, pickerPage + 1);
        }
    };

    const handleBack = () => {
        navigate('/admin/event');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formName.trim()) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please enter event name", life: 3000 });
            return;
        }
        if (!formDate) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please select event date", life: 3000 });
            return;
        }
        if (!formVenue.trim()) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please enter event venue", life: 3000 });
            return;
        }
        if (!id && !notifyAllUsers && (notifyPush || notifyWhatsApp) && selectedUsers.length === 0) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please select at least one user", life: 3000 });
            return;
        }

        const payload: any = {
            name: formName.trim(),
            namegujarati: formNameGu.trim(),
            description: formDescription.trim(),
            event_date: formDate,
            event_time: formTime,
            venue: formVenue.trim(),
            venuegujarati: formVenueGu.trim(),
        };

        if (!id && (notifyPush || notifyWhatsApp)) {
            payload.notify_push = notifyPush;
            payload.notify_whatsapp = notifyWhatsApp;
            payload.target_type = notifyAllUsers ? "all_users" : "selected_users";
            payload.userIds = notifyAllUsers ? [] : selectedUsers.map((u) => u.id);
        }

        try {
            setSaving(true);

            const response = id
                ? await apiFunctions.patch(`/event/${id}`, payload)
                : await apiFunctions.post("/event", payload);

            if (response?.data?.success) {
                showToast({
                    severity: "success",
                    summary: "Success",
                    detail: response.data.message,
                    life: 4000,
                });
                navigate("/admin/event");
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response?.data?.message || "Something went wrong",
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="back-button" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <div className="page-title">
                {id ? (<h3>Edit Event</h3>) : (<h3>Add Event</h3>)}
            </div>
            <form onSubmit={handleSubmit}>
                <Card className="grid form-layout">
                    {loading ? (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="160px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="160px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12">
                                    <Skeleton width="90px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="80px" borderRadius="6px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="90px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="90px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="130px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="130px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="field mt-5 mb-4">
                                    <Skeleton width="90px" height="40px" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Event Name (English) *</label>
                                    <PrimeInputText
                                        type="text"
                                        value={formName}
                                        onChange={(e) => setFormName(e.target.value)}
                                        placeholder="Enter event name"
                                        className="w-full mt-1"
                                        autoFocus
                                    />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Event Name (ગુજરાતી)</label>
                                    <PrimeInputText
                                        type="text"
                                        value={formNameGu}
                                        onChange={(e) => setFormNameGu(e.target.value)}
                                        placeholder="કાર્યક્રમનું નામ"
                                        className="w-full mt-1"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12">
                                    <label className="form-label">Description</label>
                                    <InputTextarea
                                        value={formDescription}
                                        onChange={(e) => setFormDescription(e.target.value)}
                                        rows={4}
                                        autoResize
                                        placeholder="Enter description (optional)"
                                        className="w-full mt-1"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Event Date *</label>
                                    <Calendar
                                        value={formDate}
                                        onChange={(e) => setFormDate(e.value as Date | null)}
                                        dateFormat="dd/mm/yy"
                                        placeholder="Select date"
                                        className="w-full mt-1"
                                        showIcon
                                    />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Event Time</label>
                                    <input
                                        type="time"
                                        value={formTime}
                                        onChange={(e) => setFormTime(e.target.value)}
                                        className="p-2 border rounded w-full mt-1"
                                        style={{ height: "42px", boxSizing: "border-box" }}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Venue (English) *</label>
                                    <PrimeInputText
                                        type="text"
                                        value={formVenue}
                                        onChange={(e) => setFormVenue(e.target.value)}
                                        placeholder="Enter venue"
                                        className="w-full mt-1"
                                    />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Venue (ગુજરાતી)</label>
                                    <PrimeInputText
                                        type="text"
                                        value={formVenueGu}
                                        onChange={(e) => setFormVenueGu(e.target.value)}
                                        placeholder="સ્થળ"
                                        className="w-full mt-1"
                                    />
                                </div>
                            </div>

                            {!id && (
                                <div className="grid grid-cols-12 gap-4">
                                    <div className="col-span-12 flex flex-col gap-2 border rounded p-3" style={{ borderColor: "#d1d5db" }}>
                                        <label className="form-label">Notifications</label>

                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="notify-push"
                                                checked={notifyPush}
                                                onChange={(e) => setNotifyPush(e.target.checked)}
                                            />
                                            <label htmlFor="notify-push" className="text-sm flex items-center gap-1">
                                                <BellIcon style={{ width: 15, height: 15 }} />
                                                Send Firebase push notification
                                            </label>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="notify-whatsapp"
                                                checked={notifyWhatsApp}
                                                onChange={(e) => setNotifyWhatsApp(e.target.checked)}
                                            />
                                            <label htmlFor="notify-whatsapp" className="text-sm flex items-center gap-1">
                                                <span style={{ color: "#25D366", display: "inline-flex" }}><WhatsAppIcon size={14} /></span>
                                                Send WhatsApp message (Gujarati template)
                                            </label>
                                        </div>

                                        {(notifyPush || notifyWhatsApp) && (
                                            <>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <input
                                                        type="checkbox"
                                                        id="notify-all"
                                                        checked={notifyAllUsers}
                                                        onChange={(e) => setNotifyAllUsers(e.target.checked)}
                                                    />
                                                    <label htmlFor="notify-all" className="text-sm">Send to all users</label>
                                                </div>

                                                {!notifyAllUsers && (
                                                    <div className="flex flex-col gap-2 border rounded p-2" style={{ borderColor: "#e5e7eb" }}>
                                                        <div className="flex items-center justify-between gap-2">
                                                            <PrimeInputText
                                                                type="text"
                                                                value={pickerSearch}
                                                                onChange={(e) => setPickerSearch(e.target.value)}
                                                                placeholder="Search users..."
                                                                className="w-full"
                                                                style={{ padding: "6px 10px" }}
                                                            />
                                                            <span className="text-xs whitespace-nowrap font-medium" style={{ color: "#16a34a" }}>
                                                                {selectedUsers.length} selected
                                                            </span>
                                                        </div>

                                                        <div
                                                            style={{ maxHeight: "220px", overflowY: "auto" }}
                                                            className="flex flex-col gap-1"
                                                            onScroll={handlePickerScroll}
                                                        >
                                                            {pickerLoading ? (
                                                                <span className="text-xs text-center py-3" style={{ color: "#9ca3af" }}>Loading users...</span>
                                                            ) : pickerUsers.length === 0 ? (
                                                                <span className="text-xs text-center py-3" style={{ color: "#9ca3af" }}>No users found</span>
                                                            ) : (
                                                                pickerUsers.map((u: any) => {
                                                                    const isSelected = selectedUsers.some((s) => s.id === u.id);
                                                                    return (
                                                                        <div key={u.id} className="flex items-center gap-2 px-1 py-1 rounded" style={{ background: isSelected ? "#f0fdf4" : "transparent" }}>
                                                                            <Checkbox
                                                                                checked={isSelected}
                                                                                onChange={(e) => {
                                                                                    if (e.checked) {
                                                                                        setSelectedUsers((prev) => [...prev, u]);
                                                                                    } else {
                                                                                        setSelectedUsers((prev) => prev.filter((s) => s.id !== u.id));
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <div className="flex-1 min-w-0">
                                                                                <span className="text-sm">{u.name} {u.surname}</span>
                                                                                <span className="text-xs ml-2" style={{ color: "#9ca3af" }}>{u.mobileno || ""}</span>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })
                                                            )}
                                                            {pickerLoadingMore && (
                                                                <span className="text-xs text-center py-2" style={{ color: "#9ca3af" }}>Loading more...</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-12 gap-4">
                                <div className="field mt-5 mb-4">
                                    <Button
                                        label={id ? "Update" : "ADD"}
                                        className="prime-button btn-login w-max"
                                        loading={saving}
                                        type="submit"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </Card>
            </form>
        </>
    );
};

export default EventCreate;
